import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { animalesAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GlobalSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Keyboard shortcut: Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) { setQuery(''); setResults([]); }
  }, [open]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 1 || !user?.establecimiento_id) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await animalesAPI.buscarGlobal(user.establecimiento_id, q);
      setResults(res.data?.resultados || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
  };

  const handleSelect = (animal) => {
    setOpen(false);
    navigate(`/animales/${animal.id}`);
  };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-gray-400 text-sm transition"
        data-testid="global-search-btn"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span className="hidden sm:inline">Buscar VID...</span>
        <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono bg-gray-700 rounded text-gray-400">Ctrl+K</kbd>
      </button>

      {/* Modal overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50 backdrop-blur-sm">
          <div ref={containerRef} className="w-full max-w-lg mx-4 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden" data-testid="global-search-modal">
            {/* Input */}
            <div className="flex items-center px-4 border-b border-gray-700">
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleChange}
                placeholder="Buscar por VID, IDE o propietario..."
                className="flex-1 bg-transparent text-white placeholder-gray-500 px-3 py-4 text-base outline-none"
                data-testid="global-search-input"
              />
              {loading && <div className="w-5 h-5 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />}
              <kbd className="ml-2 px-2 py-1 text-xs font-mono bg-gray-800 rounded text-gray-500 cursor-pointer" onClick={() => setOpen(false)}>ESC</kbd>
            </div>

            {/* Results */}
            <div className="max-h-80 overflow-y-auto">
              {query.length > 0 && results.length === 0 && !loading && (
                <p className="px-4 py-6 text-center text-gray-500 text-sm">
                  No se encontraron animales para "{query}"
                </p>
              )}
              {results.map((animal) => (
                <button
                  key={animal.id}
                  onClick={() => handleSelect(animal)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-800 transition text-left"
                  data-testid={`search-result-${animal.VID}`}
                >
                  <span className="text-xl flex-shrink-0">
                    {animal.sexo === 'hembra' ? '\u{1F404}' : '\u{1F402}'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      VID: <span className="text-green-400">{animal.VID}</span>
                      {animal.IDE && <span className="text-gray-500 text-xs ml-2">IDE: {animal.IDE}</span>}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {animal.sexo} · {animal.categoria?.replace('_', ' ')} · {animal.raza || ''}
                      {animal.propietario && ` · ${animal.propietario}`}
                    </p>
                  </div>
                  <span className={`flex-shrink-0 px-2 py-0.5 text-xs rounded-full ${animal.estado === 'activo' ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'}`}>
                    {animal.estado}
                  </span>
                </button>
              ))}
            </div>

            {/* Footer */}
            {query.length === 0 && (
              <div className="px-4 py-3 border-t border-gray-800 text-xs text-gray-500">
                Escribi VID, IDE o nombre de propietario para buscar
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
